import React from 'react';
import { MessageSquare, Zap, ShieldCheck, Sparkles } from 'lucide-react';
import { LogoIcon } from '@/components/common/Logo';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { Badge } from '@/components/ui/badge';

interface AuthLayoutProps {
  children: React.ReactNode;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-background text-foreground">
      {/* Left Column: Branding Showcase & Ambient Hero (Visible on lg+) */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 xl:p-16 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black text-white overflow-hidden border-r border-zinc-800/80">
        {/* Ambient Glow Gradients */}
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/20 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute top-1/2 -right-24 w-80 h-80 bg-violet-600/15 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute -bottom-24 left-1/3 w-96 h-96 bg-emerald-600/10 blur-3xl rounded-full pointer-events-none" />

        {/* Top Header / Brand */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white/10 border border-white/20 shadow-lg flex items-center justify-center text-white backdrop-blur-md">
            <LogoIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold tracking-tight text-white">Tales</span>
              <Badge variant="outline" className="text-xs bg-white/10 text-zinc-300 border-white/20 px-2 py-0.5">
                Enterprise
              </Badge>
            </div>
            <p className="text-xs text-zinc-400">Atendimento com Agente de IA para E-commerce</p>
          </div>
        </div>

        {/* Hero Value Proposition */}
        <div className="relative z-10 space-y-6 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs text-zinc-200 backdrop-blur-md">
            <Sparkles className="h-3.5 w-3.5 text-violet-400" />
            <span>Agente de IA para E-commerce</span>
          </div>

          <h1 className="text-3xl xl:text-4xl font-extrabold tracking-tight text-white leading-tight">
            Transforme o atendimento da sua loja com Agente de IA 24/7.
          </h1>

          <p className="text-sm xl:text-base text-zinc-300 leading-relaxed">
            Automatize dúvidas frequentes da Shopify e Nuvemshop com seu Agente de IA e assuma a conversa com sua equipe sempre que precisar de atendimento humano.
          </p>

          {/* Key Feature Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4">
            <div className="p-3.5 rounded-lg bg-white/5 border border-white/10 backdrop-blur-sm space-y-1">
              <Zap className="h-4 w-4 text-amber-400" />
              <div className="text-xs font-semibold text-white">Resposta Instantânea</div>
              <div className="text-[11px] text-zinc-400">&lt; 1s de resposta média do Agente</div>
            </div>

            <div className="p-3.5 rounded-lg bg-white/5 border border-white/10 backdrop-blur-sm space-y-1">
              <MessageSquare className="h-4 w-4 text-sky-400" />
              <div className="text-xs font-semibold text-white">Respostas na Hora</div>
              <div className="text-[11px] text-zinc-400">Mensagens e pedidos atualizados em tempo real</div>
            </div>

            <div className="p-3.5 rounded-lg bg-white/5 border border-white/10 backdrop-blur-sm space-y-1">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              <div className="text-xs font-semibold text-white">Atendimento Humano</div>
              <div className="text-[11px] text-zinc-400">Assumir conversa com 1 clique</div>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div className="relative z-10 pt-6 border-t border-white/10 flex items-center justify-between text-xs text-zinc-400">
          <span>&copy; {new Date().getFullYear()} Plataforma Tales</span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            Sistema Operacional
          </span>
        </div>
      </div>

      {/* Right Column: Interactive Form Container */}
      <div className="relative flex flex-col justify-between p-6 sm:p-10 lg:p-12">
        {/* Top Right Quick Theme Switcher & Mobile Brand Header */}
        <div className="flex items-center justify-between w-full">
          <div className="flex lg:hidden items-center gap-2.5">
            <LogoIcon className="h-7 w-7 text-primary" />
            <span className="text-base font-bold tracking-tight">Tales</span>
          </div>

          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>

        {/* Centered Form Content */}
        <div className="w-full max-w-md mx-auto my-auto py-8">
          {children}
        </div>

        {/* Bottom subtle legal links */}
        <div className="text-center text-xs text-muted-foreground pt-4">
          Ao entrar, você concorda com nossos Termos de Serviço e Política de Privacidade.
        </div>
      </div>
    </div>
  );
};
